import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Descriptions, Badge, Button, Space, Card, Typography, message, Avatar } from "antd";
import { User, Mail, CreditCard, Calendar, FileText, Briefcase } from "react-feather";
import { useQuery, useQueryClient } from "react-query";
import { api_srv } from "services";

const { Title, Text } = Typography;

export default function ViewUnderwriter() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isMounted = useRef(true);

  // Mock data for testing
  const mockUnderwriterData = {
    id: "1",
    underwriterSystemRef: "UW-2024-001",
    name: "Ianlam Insurance",
    email: "contact@ianlam.com",
    status: "active",
    settlementType: "bank",
    bankName: "Kenya Commercial Bank",
    accountNumber: "1234567890",
    branch: "Westlands Branch",
    settlementFrequency: "Monthly",
    createdDate: "2024-01-15T10:30:00Z",
    createdBy: "admin@system.com",
    avatar: "https://ui-avatars.com/api/?name=Ianlam+Insurance&background=1890ff&color=fff&size=100",
    totalProducts: 5,
    totalCommissionEarned: 750000,
    pendingSettlement: 250000,
    totalPolicies: 20,
    premium: {
      total: 5000000,
      settled: 4000000,
      pending: 1000000
    },
    commission: {
      total: 2000000,
      settlement: 1000000,
      pending: 1000000,
      settled: 500000
    },
    policy: {
      number: 20,
      volume: 10000000
    }
  };

  const get_underwriter_details = async () => {
    // For testing, return mock data
    return mockUnderwriterData;
    
    // Uncomment below for actual API call
    // let resp = await (await api_srv).fetchUnderwriterDetails(id);
    // console.log("res underwriter details---", resp);
    // return resp.data;
  };

  const {
    data: underwriterData,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["underwriter_details", id],
    queryFn: get_underwriter_details,
    staleTime: 5 * 60 * 1000,
    retry: 3,
    onSettled: () => {
      if (!isMounted.current) return;
    },
  });

  useEffect(() => {
    console.log(`Fetching underwriter details for ID: ${id}`);
    
    return () => {
      isMounted.current = false;
      queryClient.cancelQueries(["underwriter_details", id]);
    };
  }, [id, queryClient]);

  if (isLoading || isFetching) {
    return <div>Loading underwriter...</div>;
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  if (!underwriterData) {
    return <div>No underwriter data found</div>;
  }

  const underwriter = underwriterData;

  const getStatusColor = (status) => {
    const statusColors = {
      active: '#52c41a',
      inactive: '#ff4d4f',
      pending: '#faad14',
    };
    return statusColors[status?.toLowerCase()] || '#52c41a';
  };

  const handleRequest = async () => {
    try {
      message.success('Request sent successfully');
    } catch (error) {
      message.error('Failed to send request');
    }
  };

  const handleDeactivate = async () => {
    try {
      message.success('Underwriter deactivated');
      queryClient.invalidateQueries(["underwriter_details", id]);
    } catch (error) {
      message.error('Failed to deactivate underwriter');
    }
  };

  const handleMakePayment = async () => {
    try {
      message.success('Payment initiated successfully');
    } catch (error) {
      message.error('Failed to initiate payment');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
      {/* Top Section - Profile */}
      <Card 
        style={{ marginBottom: '24px', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Avatar 
            size={100} 
            src={underwriter.avatar}
            style={{ backgroundColor: '#1890ff' }}
          >
            {underwriter.name.charAt(0)}
          </Avatar>
          
          <div style={{ flex: 1 }}>
            <Title level={2} style={{ margin: 0, color: '#1f2937', fontWeight: 600 }}>
              {underwriter.name}
            </Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
              <Badge 
                color={getStatusColor(underwriter.status)} 
                text={underwriter.status.toUpperCase()}
                style={{ fontSize: '12px', fontWeight: '600' }}
              />
              <Text type="secondary">Created by: {underwriter.createdBy}</Text>
              <Text type="secondary">
                Created: {new Date(underwriter.createdDate).toLocaleDateString()}
              </Text>
            </div>
          </div>
          
          <Space size="middle">
            <Button type="primary" size="large" onClick={handleRequest}>
              Request
            </Button>
            <Button 
              type="default" 
              size="large"
              onClick={handleDeactivate}
              disabled={underwriter.status === 'inactive'}
            >
              Deactivate
            </Button>
          </Space>
        </div>
      </Card>

      {/* Underwriter Details Section */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
          <User size={20} style={{ color: '#1890ff' }} />
          <Title level={4} style={{ margin: 0, color: '#1f2937' }}>Underwriter Details</Title>
        </div>
        <Descriptions 
          bordered 
          column={2}
          size="middle"
          labelStyle={{ width: '20%', fontWeight: 500, background: '#fafafa' }}
          contentStyle={{ background: '#fff' }}
          style={{ borderRadius: '8px' }}
        >
          <Descriptions.Item label="System Reference">
            <span>{underwriter.underwriterSystemRef}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <span>{underwriter.email}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Settlement Type">
            <span>{underwriter.settlementType}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Settlement Frequency">
            <span>{underwriter.settlementFrequency}</span>
          </Descriptions.Item>
          {underwriter.settlementType === "bank" && (
            <>
              <Descriptions.Item label="Bank Name">
                <span>{underwriter.bankName}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Account Number">
                <span>{underwriter.accountNumber}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Branch" span={2}>
                <span>{underwriter.branch}</span>
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      {/* Summary Cards Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText size={24} style={{ color: '#1890ff' }} />
              <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Products</Title>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {underwriter.totalProducts}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>Total Products</Text>
            </div>
          </div>
        </Card>

        <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Briefcase size={24} style={{ color: '#52c41a' }} />
              <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Commission Earned</Title>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#52c41a' }}>
                KES {underwriter.totalCommissionEarned.toLocaleString()}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>Total Earned</Text>
            </div>
          </div>
        </Card>

        <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CreditCard size={24} style={{ color: '#faad14' }} />
              <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Pending Settlement</Title>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#faad14' }}>
                KES {underwriter.pendingSettlement.toLocaleString()}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>Outstanding</Text>
            </div>
          </div>
        </Card>

        <Card style={{ borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText size={24} style={{ color: '#722ed1' }} />
              <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Policies</Title>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#722ed1' }}>
                {underwriter.totalPolicies}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>Active Policies</Text>
            </div>
          </div>
        </Card>
      </div>

      {/* Make Payment Button */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <Button 
          type="primary" 
          size="large" 
          onClick={handleMakePayment}
          style={{ paddingLeft: '32px', paddingRight: '32px' }}
        >
          Make Payment
        </Button>
      </div>
    </div>
  );
}